import { IKitchenDish } from 'types/kitchen/kitchenDish.interface'
import { IKitchenIngredient } from 'types/kitchen/kitchenIngredient.interface'
import { IKitchenMenuDay, IKitchenMenuTemplate } from 'types/kitchen/kitchenMenu.interface'
import { IKitchenSite } from 'types/kitchen/kitchenSite.interface'

export const KITCHEN_DIETARY_TAGS = ['Regular', 'Vegetarian', 'Halal', 'Gluten-Free', 'Nut Allergy']

export const KITCHEN_SUPPLIERS = ['Fresh Foods Ltd', 'Akwaaba Catering Ltd', 'Kumasi Bakery', 'Atlantic Fish Co']

export const KITCHEN_WINDOWS = ['Breakfast', 'Lunch', 'Dinner']

export const KITCHEN_TAG_SETS: { [kitchenId: number]: string[] } = {
  1: ['Regular', 'Vegetarian'],
  2: ['Regular', 'Halal', 'Vegetarian'],
}

export const kitchenSitesMock: IKitchenSite[] = [
  { id: 1, name: 'Main Kitchen', supplyMode: 'CLIENT_SUPPLIED', variancePct: 2.1 },
  { id: 2, name: 'North Kitchen', supplyMode: 'CONTRACTOR_SUPPLIED', contractor: 'Akwaaba Catering Ltd' },
]

export const kitchenIngredientsMock: IKitchenIngredient[] = [
  { id: 1, name: 'Rice', unit: 'kg', recipeUnit: 'g', factor: 1000, stock: 45, price: 1.8, batches: [{ qty: 45, exp: '12/3' }] },
  { id: 2, name: 'Chicken', unit: 'kg', recipeUnit: 'g', factor: 1000, stock: 14, price: 6.5, batches: [{ qty: 14, exp: '23/2', soon: true }] },
  { id: 3, name: 'Pasta', unit: 'kg', recipeUnit: 'g', factor: 1000, stock: 14, price: 2.2, batches: [{ qty: 8, exp: '22/2', soon: true }, { qty: 6, exp: '28/2' }] },
  { id: 4, name: 'Tomato Sauce', unit: 'L', recipeUnit: 'ml', factor: 1000, stock: 9, price: 3.1, batches: [{ qty: 9, exp: '26/2' }] },
  { id: 5, name: 'Tilapia', unit: 'kg', recipeUnit: 'g', factor: 1000, stock: 9, price: 7.2, batches: [{ qty: 9, exp: '21/2', critical: true }] },
  { id: 6, name: 'Onion', unit: 'kg', recipeUnit: 'g', factor: 1000, stock: 12, price: 1.4, batches: [{ qty: 12, exp: '4/3' }] },
  { id: 7, name: 'Beans', unit: 'kg', recipeUnit: 'g', factor: 1000, stock: 20, price: 2.0, batches: [{ qty: 20, exp: '20/4' }] },
  { id: 8, name: 'Cooking Oil', unit: 'L', recipeUnit: 'ml', factor: 1000, stock: 18, price: 4.0, batches: [{ qty: 18, exp: '30/5' }] },
]

export const kitchenDishesMock: IKitchenDish[] = [
  { id: 1, name: 'Jollof Rice & Chicken', type: 'Prepared', tags: ['Regular'], active: true, norm: [{ ing: 1, qty: 180 }, { ing: 2, qty: 150 }, { ing: 6, qty: 30 }, { ing: 8, qty: 20 }] },
  { id: 2, name: 'Vegetable Pasta', type: 'Prepared', tags: ['Regular', 'Vegetarian'], active: true, norm: [{ ing: 3, qty: 120 }, { ing: 4, qty: 80 }, { ing: 6, qty: 20 }, { ing: 8, qty: 10 }] },
  { id: 3, name: 'Banku & Tilapia', type: 'Prepared', tags: ['Regular'], active: true, norm: [{ ing: 5, qty: 140 }, { ing: 6, qty: 25 }, { ing: 8, qty: 15 }] },
  { id: 4, name: 'Beans Stew', type: 'Prepared', tags: ['Regular', 'Vegetarian'], active: true, norm: [{ ing: 7, qty: 160 }, { ing: 6, qty: 25 }, { ing: 8, qty: 15 }] },
  { id: 5, name: 'Sandwich Pack (Standby)', type: 'Purchased', tags: ['Regular'], active: true, supplier: 'Fresh Foods Ltd', norm: [] },
  { id: 6, name: 'Halal Beef Stew (Contractor)', type: 'Purchased', tags: ['Regular', 'Halal'], active: true, supplier: 'Akwaaba Catering Ltd', norm: [] },
]

export const buildKitchenMenuDays = (weekStart: Date, today = new Date(2028, 1, 20)): IKitchenMenuDay[] => {
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
  const tomorrowMid = todayMid + 86400000

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i)
    const mid = d.getTime()
    const dStr = `${d.getDate()}/${d.getMonth() + 1}`
    const isToday = mid === todayMid
    const isTomorrow = mid === tomorrowMid
    let kind: IKitchenMenuDay['kind'] = 'future'
    if (mid < todayMid) kind = 'past'
    else if (isToday) kind = 'today'
    else if (isTomorrow) kind = 'tomorrow'

    const label = isToday ? `${weekdays[i]} ${d.getDate()} · Today` : `${weekdays[i]} ${d.getDate()}`

    return { d: dStr, label, kind }
  })
}

export const kitchenMenuDaysMock: IKitchenMenuDay[] = [
  { d: '16/2', label: 'Mon 16', kind: 'past' },
  { d: '17/2', label: 'Tue 17', kind: 'past' },
  { d: '18/2', label: 'Wed 18', kind: 'past' },
  { d: '19/2', label: 'Thu 19', kind: 'past' },
  { d: '20/2', label: 'Fri 20 · Today', kind: 'today' },
  { d: '21/2', label: 'Sat 21', kind: 'tomorrow' },
  { d: '22/2', label: 'Sun 22', kind: 'future' },
]

export const kitchenInitialAssignMock: { [key: string]: number } = {
  '1-20/2-Breakfast-Regular': 1,
  '1-20/2-Breakfast-Vegetarian': 4,
  '1-20/2-Lunch-Regular': 1,
  '1-20/2-Lunch-Vegetarian': 2,
  '1-20/2-Dinner-Vegetarian': 4,
  '1-21/2-Lunch-Regular': 3,
  '1-21/2-Lunch-Vegetarian': 2,
  '2-20/2-Lunch-Regular': 6,
  '2-20/2-Lunch-Halal': 6,
  '2-20/2-Lunch-Vegetarian': 2,
}

export const kitchenInitialStatusMock: { [key: string]: string } = {
  '1-20/2-Breakfast': 'Published',
  '1-20/2-Lunch': 'Published',
  '1-20/2-Dinner': 'Draft',
  '1-21/2-Lunch': 'Draft',
  '2-20/2-Lunch': 'Published',
}

export const kitchenTemplatesMock: { [id: number]: IKitchenMenuTemplate } = {
  1: {
    id: 1,
    name: 'Kumasi 14-Day Cycle (Main)',
    length: 14,
    cycle: {
      Breakfast: { Regular: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], Vegetarian: [4, 2, 4, 2, 4, 2, 4, 2, 4, 2, 4, 2, 4, 2] },
      Lunch: { Regular: [1, 3, 1, 4, 3, 1, 3, 1, 3, 4, 1, 3, 1, 3], Vegetarian: [2, 4, 2, 4, 2, 4, 2, 4, 2, 4, 2, 4, 2, 4] },
      Dinner: { Regular: [3, 1, 4, 1, 3, 4, 1, 3, 1, 3, 4, 1, 3, 1], Vegetarian: [4, 2, 4, 2, 4, 2, 4, 2, 4, 2, 4, 2, 4, 2] },
    },
  },
  2: {
    id: 2,
    name: 'Akwaaba Contractor Cycle (North)',
    length: 7,
    cycle: {
      Breakfast: { Regular: [6, 6, 6, 6, 6, 6, 6], Halal: [6, 6, 6, 6, 6, 6, 6], Vegetarian: [2, 2, 2, 2, 2, 2, 2] },
      Lunch: { Regular: [6, 6, 6, 6, 6, 6, 6], Halal: [6, 6, 6, 6, 6, 6, 6], Vegetarian: [2, 2, 2, 2, 2, 2, 2] },
      Dinner: { Regular: [6, 6, 6, 6, 6, 6, 6], Halal: [6, 6, 6, 6, 6, 6, 6], Vegetarian: [2, 2, 2, 2, 2, 2, 2] },
    },
  },
}

export const kitchenPastMenuMock: { [window: string]: number[] } = {
  Breakfast: [1, 4],
  Lunch: [1, 2],
  Dinner: [3, 4],
}

export const kitchenSuggestionReasons: { [dishId: number]: string } = {
  3: 'Consumes Tilapia 9 kg expiring 21/2 (critical)',
  1: 'Consumes Chicken 14 kg expiring 23/2',
  2: 'Consumes Pasta batch 8 kg expiring 22/2',
  4: 'No expiry pressure — stable stock',
}

export const getKitchenIngredient = (ingredients: IKitchenIngredient[], id: number) =>
  ingredients.find((i) => i.id === id)

export const kitchenCostPerPortion = (dish: IKitchenDish, ingredients: IKitchenIngredient[]) =>
  dish.norm.reduce((s, l) => {
    const i = getKitchenIngredient(ingredients, l.ing)
    if (!i) return s
    return s + (l.qty / i.factor) * i.price
  }, 0)

export const kitchenCookable = (dish: IKitchenDish, ingredients: IKitchenIngredient[]) => {
  if (dish.norm.length === 0) return null
  return Math.min(
    ...dish.norm.map((l) => {
      const i = getKitchenIngredient(ingredients, l.ing)
      if (!i) return 0
      return Math.floor((i.stock * i.factor) / l.qty)
    }),
  )
}

export const kitchenExpiryScore = (dish: IKitchenDish, ingredients: IKitchenIngredient[]) => {
  if (dish.norm.some((l) => getKitchenIngredient(ingredients, l.ing)?.batches.some((b) => b.critical))) return 2
  if (dish.norm.some((l) => getKitchenIngredient(ingredients, l.ing)?.batches.some((b) => b.soon))) return 1
  return 0
}

export const kitchenDemandFor = (window: string, tagSet: string) => {
  const base: { [w: string]: { [t: string]: number } } = {
    Breakfast: { Regular: 110, Vegetarian: 28, Halal: 10 },
    Lunch: { Regular: 96, Vegetarian: 24, Halal: 12 },
    Dinner: { Regular: 84, Vegetarian: 24, Halal: 10 },
  }
  return base[window]?.[tagSet] || 0
}
